import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import PropertyAddUserMutation from 'src/mutations/PropertyAddUserMutation';
import FlashesStore from 'src/stores/FlashesStore';
import { USER_TYPE_CUSTOMER } from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import isActive from 'src/util/isActive';

import AdminPropertyUserAddForm from './AdminPropertyUserAddForm';

const AdminPropertyUserAddFragment = graphql`
  fragment AdminPropertyUserAdd_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminPropertyUserAddRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  ) {
    id
    properties(first: $count, after: $cursor)
    @connection(key: "AdminPropertyUserAdd_properties") {
      edges {
        cursor
        node {
          uuid
          title
          address { line1 line2 city state postcode }
          active { start finish }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

const AdminPropertyUserAddUsersFragment = graphql`
  fragment AdminPropertyUserAddUsers_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminPropertyUserAddUsersRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    id
    users(first: $count, after: $cursor)
    @connection(key: "AdminPropertyUserAddUsers_users") {
      edges {
        cursor
        node {
          uuid
          type
          givenName
          familyName
          email
          active { start finish }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

/**
 * Admin form for adding a customer user to a property. Field errors, and a
 * resolved query without a viewer, throw to the route's PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {{push: Function}} props.router - Router used to return to the property users page.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactElement} AdminPropertyUserAdd component.
 */
function AdminPropertyUserAdd({ router, viewer = null }) {
  const intl = useIntl();

  const [processing, setProcessing] = useState(false);

  const {
    data: propertiesData,
    loadNext: loadNextProperties,
    hasNext: hasNextProperties,
    isLoadingNext: isLoadingNextProperties,
  } = usePaginationFragment(
    AdminPropertyUserAddFragment,
    viewer,
  );

  const {
    data: usersData,
    loadNext: loadNextUsers,
    hasNext: hasNextUsers,
    isLoadingNext: isLoadingNextUsers,
  } = usePaginationFragment(
    AdminPropertyUserAddUsersFragment,
    viewer,
  );

  if (!viewer) {
    // A completed query without a viewer would otherwise render an empty form.
    throw new Error('AdminPropertyUserAdd: the route query returned no viewer');
  }

  const mutationName = 'propertyAddUser';

  const handleAdminPropertyUserAddSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((err) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [err],
        },
      }, mutationName));
      return;
    }

    const { uuid } = response.propertyAddUser;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      intl.formatMessage(
        { id: 'admin.admin_property_user.admin_property_user_add.response_message.success', defaultMessage: 'Successfully added Property User {uuidWithLink}' },
        {
          uuidWithLink: (
            <UUID uuid={uuid} url={`/admin/property-users/${uuid}`} />
          ),
        },
      ),
      mutationName,
    );

    router.push('/admin/property-users/');
  };

  const handleAdminPropertyUserAddFailure = (err) => {
    setProcessing(false);

    FlashesStore.flash(FlashesStore.ERROR, err, mutationName);
  };

  const handleAdminPropertyUserAdd = (propertyId, userId) => {
    setProcessing(true);
    FlashesStore.reset();

    PropertyAddUserMutation(
      propertyId,
      userId,
      handleAdminPropertyUserAddSuccess,
      handleAdminPropertyUserAddFailure,
    );
  };

  const propertyNodes = getNodesFromEdges(propertiesData.properties);
  const activeProperties = propertyNodes.filter((property) => isActive(property?.active));
  const userNodes = getNodesFromEdges(usersData.users);
  const activeUsers = userNodes.filter((user) => isActive(user?.active));
  const customerUsersList = activeUsers.filter((user) => user.type === USER_TYPE_CUSTOMER);

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.page.title', defaultMessage: 'Enosi Admin - Property Users - Add Property User' },
  );
  const breadcrumbAdmin = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbPropertyUsers = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.property_users.label', defaultMessage: 'Property Users' },
  );
  const breadcrumbAddPropertyUser = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_add.breadcrumbs.add_property_user.label', defaultMessage: 'Add Property User' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPropertyUsers, path: '/admin/property-users' },
          { name: breadcrumbAddPropertyUser },
        ]}
      />

      <AdminPropertyUserAddForm
        handleAddFunc={handleAdminPropertyUserAdd}
        processing={processing}
        properties={activeProperties}
        users={customerUsersList}
        loadNextPropertiesFunc={loadNextProperties}
        hasNextProperties={hasNextProperties}
        isLoadingNextProperties={isLoadingNextProperties}
        loadNextUsersFunc={loadNextUsers}
        hasNextUsers={hasNextUsers}
        isLoadingNextUsers={isLoadingNextUsers}
      />
    </>
  );
}

AdminPropertyUserAdd.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminPropertyUserAdd;
